import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AbstractInputDirective } from '../abstract-input/abstract-input.component';
import { TextInputType } from './models/text-input-type.enum';
import { TextInputTypeLiteral } from './models/text-input-type.literal';

/**
 * The Nomidio Text Input component presents a text input component to the user.
 *
 * Text Inputs also accept the following CSS configuration:
 *
 * * `--input-header-font-weight`: any valid `font-weight` value. Set the font weight of the input header, i.e. the text label.
 *
 * @example Basic usage
 * <nom-text-input [control]="someFormControl"></nom-text-input>
 *
 * @example Password field
 * <nom-text-input [control]="password" type="password"></nom-text-input>
 *
 * @example Value-only usage
 * <nom-text-input [value]="someImportantValue"></nom-text-input>
 *
 * @example Configure the label's font weight in CSS
 * nom-text-input {
 *   --input-header-font-weight: bold;
 * }
 * <nom-text-input [control]="control"></nom-text-input>
 */
@Component({
    selector: 'nom-text-input',
    templateUrl: './text-input.component.html',
    styleUrls: ['./text-input.component.scss'],
    standalone: false
})
export class TextInputComponent<T = any> extends AbstractInputDirective<T> implements OnInit, OnChanges {
  /**
   * The HTML input type.
   */
  @Input()
  public type: TextInputTypeLiteral = TextInputType.Text;

  /**
   * The label to use in the Show/Hide button for password fields.
   *
   * @example showHideLabel="Foo" // produces "Show Foo" / "Hide Foo"
   */
  @Input()
  public showHideLabel = 'Password';

  /**
   * The form placeholder, if any.
   */
  @Input()
  public placeholder = '';

  /**
   * The value to present. Use this OR control, not both.
   */
  @Input()
  public set value(newValue: T) {
    if (!this.control) {
      this.control = new FormControl(newValue, { nonNullable: true });
    }
    this.control.setValue(newValue);
  }

  public get value(): T {
    return this.control.value;
  }

  /**
   * Whether the input is currently masked.
   * @internal
   */
  public masked = false;

  public ngOnInit() {
    if (this.isPassword) {
      this.masked = true;
    }
  }

  /**
   * Verifies that the component has received only a Value input or only a Control input and not both.
   * @internal
   *
   * @param changes The component changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    const hasValue = changes['value'] && typeof changes['value'].currentValue !== undefined;
    const hasControl = changes['control'] && typeof changes['control'].currentValue !== undefined;
    if (hasValue && hasControl) {
      throw Error(`Both control and value inputs were supplied to this text-input.`
        + ` Please only use one or the other; these properties will conflict.`
        + ` Input ID: ${this.inputId}`);
    }
  }

  /**
   * Get whether this component represents a password field.
   * @internal
   */
  public get isPassword(): boolean {
    return this.type === 'password';
  }

  /**
   * Get the form control type to display in the UI.
   *
   * If this is a password field, this will return 'password' or 'text' depending on whether the field is masked.
   * @internal
   */
  public get formControlType(): TextInputTypeLiteral {
    if (this.isPassword) {
      return this.masked ? 'password' : 'text';
    } else {
      return this.type;
    }
  }

  /**
   * Toggle whether this password field is masked.
   * @internal
   */
  public toggleMasking(): void {
    this.masked = !this.masked;
  }
}
